import React, { useState } from 'react';
import { Send, Phone, CheckCircle2, Sparkles, User, PhoneCall } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

interface ConsultationFormProps { }

export const ConsultationForm: React.FC<ConsultationFormProps> = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    areaSize: '100-300',
    businessType: '임대',
    message: '',
    privacyAgreed: true,
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    let formatted = raw;
    if (raw.length > 3 && raw.length <= 7) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
    } else if (raw.length > 7) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7, 11)}`;
    }
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('성함이나 업체명을 입력해 주세요.');
      return;
    }
    if (!formData.phone || formData.phone.length < 10) {
      alert('올바른 연락처를 입력해 주세요.');
      return;
    }
    if (!formData.privacyAgreed) {
      alert('개인정보 수집 및 이용에 동의해 주세요.');
      return;
    }

    setSubmitting(true);

    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyuQ7ObF2-CirZKOUyZLjjpbI7coroPEYMXcSmFgOsnyequ4_ViHfP-DOXSlA83dPkC/exec';

    try {
      const areaTextMap: Record<string, string> = {
        '60-100': '100평 미만',
        '100-300': '100평 ~ 300평 미만',
        '300-500': '300평 ~ 500평 미만',
        '500-1000': '500평 ~ 1,000평 미만',
      };

      const params = new URLSearchParams();
      params.append('name', formData.name);
      params.append('phone', formData.phone);
      params.append('areaSize', areaTextMap[formData.areaSize] || formData.areaSize);
      params.append('businessType', formData.businessType);
      params.append('message', formData.message || '');

      // 구글 앱스스크립트 전송 시도
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-20 sm:py-28 bg-slate-50 text-slate-900 relative border-t border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-4 leading-snug">
              전문가 상담 요청
            </h2>
            <p className="text-slate-600 text-sm sm:text-base keep-all font-medium">
              부지 평수를 선택하시면 예상 수리 지원금과 20년 임대 수익을 바로 확인하실 수 있습니다.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* Left Column: Interactive Estimator Widget & Direct Contact Info (5 cols) */}
          <div className="lg:col-span-5 space-y-5 sm:space-y-6">
            <ScrollReveal direction="up" delay={0.1}>
              {/* Real-time Yield Display Box */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-slate-200 shadow-md sm:shadow-lg relative overflow-hidden">
                <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-slate-100">
                  <div className="flex items-center gap-1.5 text-sky-600 text-xs font-extrabold tracking-wider uppercase">
                    <Sparkles className="w-4 h-4 text-sky-600" />
                    <span>2,000평 기준 예상 수익 예시</span>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-slate-50 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200">
                    <span className="text-xs font-bold text-slate-500 block mb-0.5">예상 발전 설비 용량</span>
                    <p className="text-base sm:text-lg font-black text-slate-900">약 500 ~ 600 kW (2,000평 기준)</p>
                  </div>

                  <div className="bg-sky-50/70 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-sky-200">
                    <span className="text-xs font-bold text-sky-600 block mb-0.5">지붕 수리비 지원</span>
                    <p className="text-lg sm:text-xl font-black text-sky-600">지붕 수리비 전폭 지원</p>
                  </div>

                  <div className="bg-emerald-50/70 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-emerald-200">
                    <span className="text-xs font-bold text-emerald-600 block mb-0.5">예상 수익</span>
                    <p className="text-2xl sm:text-3xl font-black text-emerald-600">최대 10억 원</p>
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 mt-3 sm:mt-4 leading-relaxed italic font-medium">
                  * 위 산정 결과는 2,000평 기준 예시이며 부지 형태 및 남향 일조량에 따라 실제 현장 방문 시 상향 조정될 수 있습니다.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.2}>
              {/* Direct Contact Person Box */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200 shadow-sm sm:shadow-md space-y-3.5 sm:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-sky-50 text-sky-600 border border-sky-200 shrink-0">
                    <PhoneCall className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-500 block">플러스에스디 전담 상담원</span>
                    <h4 className="text-base sm:text-lg font-black text-slate-900">010-2767-1449</h4>
                  </div>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed keep-all font-medium">
                  전화 한 통으로 부지 주소만 말씀해 주시면, 즉시 카카오맵 및 위성 정밀 분석을 통해 지원 가능 여부를 알려드립니다.
                </p>
                <a
                  href="tel:01027671449"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-600 font-extrabold text-xs transition-all shadow-sm cursor-pointer"
                >
                  <Phone className="w-4 h-4 text-sky-600" />
                  <span>010-2767-1449 즉시 전화 걸기</span>
                </a>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Lead Capture Form (7 cols) */}
          <div className="lg:col-span-7">
            <ScrollReveal direction="up" delay={0.15}>
              <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-10 border border-slate-200 shadow-lg sm:shadow-xl">

                {submitted ? (
                  <div className="py-12 text-center space-y-4 animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-10 h-10" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900">무료 견적 신청이 완료되었습니다!</h3>
                    <p className="text-sm text-slate-600 max-w-md mx-auto keep-all leading-relaxed font-medium">
                      입력해주신 연락처로 <strong>010-2767-1449</strong>에서 빠르게 확인 후 1:1 맞춤 수익 분석표와 함께 연락드리겠습니다.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 px-6 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700 border border-slate-300 cursor-pointer"
                    >
                      추가 견적 신청하기
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="border-b border-slate-100 pb-4 mb-2">
                      <h3 className="text-xl font-extrabold text-slate-900 tracking-tight">
                        1:1 지역 담당자 상담 요청하기
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 font-medium">
                        모든 문의 사항은 100% 대외비로 안전하게 보호됩니다.
                      </p>
                    </div>

                    {/* Form Field: Name */}
                    <div>
                      <label className="block text-xs font-extrabold text-slate-700 mb-1.5">
                        성함 / 업체명 <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          placeholder="예: 홍길동 (또는 OO공업)"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                        />
                        <User className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>

                    {/* Form Field: Phone */}
                    <div>
                      <label className="block text-xs font-extrabold text-slate-700 mb-1.5">
                        연락처 <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          required
                          placeholder="010-0000-0000"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          maxLength={13}
                          className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                        />
                        <Phone className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
                      </div>
                    </div>

                    {/* Form Field: Land Area Size */}
                    <div>
                      <label className="block text-xs font-extrabold text-slate-700 mb-1.5">
                        부지 / 지붕 평수 선택 <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={formData.areaSize}
                        onChange={(e) => setFormData({ ...formData, areaSize: e.target.value })}
                        className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors cursor-pointer"
                      >
                        <option value="60-100">100평 미만 (지붕 수리비 지원)</option>
                        <option value="100-300">100평 ~ 300평 미만 (지붕 수리비 전폭 지원)</option>
                        <option value="300-500">300평 ~ 500평 미만 (프리미엄 수리비 지원)</option>
                        <option value="500-1000">500평 ~ 1,000평 미만 (대형 맞춤 지원)</option>
                      </select>
                    </div>

                    {/* Form Field: Business Interest */}
                    <div>
                      <label className="block text-xs font-extrabold text-slate-700 mb-1.5">
                        관심 비즈니스 모델 <span className="text-rose-500">*</span>
                      </label>
                      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                        {['임대', '자가발전', '상담후 결정'].map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setFormData({ ...formData, businessType: type })}
                            className={`py-2.5 sm:py-3 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold border transition-all cursor-pointer text-center whitespace-nowrap ${formData.businessType === type
                                ? 'bg-sky-500 text-white border-sky-500 shadow-md font-extrabold'
                                : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300'
                              }`}
                          >
                            {type === '임대' && '🏢 임대사업'}
                            {type === '자가발전' && '☀️ 자가발전'}
                            {type === '상담후 결정' && '💡 상담후 결정'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Optional Message */}
                    <div>
                      <label className="block text-xs font-extrabold text-slate-700 mb-1.5">
                        부지 주소 및 메모 (선택)
                      </label>
                      <textarea
                        rows={3}
                        placeholder="예: 경기도 화성시 팔탄면 공장 지붕 누수 상태 확인 희망"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors resize-none"
                      />
                    </div>

                    {/* Privacy Checkbox */}
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="privacy"
                        checked={formData.privacyAgreed}
                        onChange={(e) => setFormData({ ...formData, privacyAgreed: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 bg-slate-50 text-sky-500 focus:ring-sky-500 cursor-pointer"
                      />
                      <label htmlFor="privacy" className="text-xs text-slate-600 font-medium cursor-pointer">
                        개인정보 수집 및 이용에 동의합니다. (상담 목적으로만 활용)
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4.5 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-black text-base transition-all shadow-lg shadow-sky-500/25 active:scale-95 cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Send className="w-5 h-5 text-white" />
                      <span>{submitting ? '신청 처리 중...' : '무료 예상 수익 분석 & 견적 신청하기'}</span>
                    </button>
                  </form>
                )}

              </div>
            </ScrollReveal>
          </div>

        </div>

      </div>
    </section>
  );
};
